import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalendarAlt, faFileAlt, faEye } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import moment from 'moment';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import DocumentViewerModal from './DocumentViewerModal';
import InvoicePdfModal from './InvoicePdfModal';
import { generateInvoicePdf } from '../utils/invoicePdfGenerator';

function Invoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf('year').toDate(),
    endDate: moment().toDate()
  });
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [dateRange]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/invoices', {
        minDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        maxDate: moment(dateRange.endDate).format('YYYY-MM-DD')
      });
      setInvoices(response.data.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = async (invoice) => {
    try {
      setSelectedInvoice(invoice);
      setLoadingPdf(true);
      
      // Fetch invoice details and client info (required)
      const [detailsRes, clientRes] = await Promise.all([
        api.post('/api/invoices/details', { TxnID: invoice.TxnID }),
        api.post('/api/invoices/client')
      ]);

      // Try to fetch documents (optional - don't fail if this errors)
      let docsData = { data: { docs: [] } };
      try {
        const docsRes = await api.post('/api/invoices/documents', { RefNumber: invoice.RefNumber });
        docsData = docsRes.data;
      } catch (docError) {
        console.warn('Could not fetch invoice documents:', docError);
        // Continue without documents
      }

      const invoiceData = {
        header: invoice,
        details: detailsRes.data.data || [],
        client: clientRes.data || {},
        docs: docsData
      };

      // Generate PDF
      const pdfResult = await generateInvoicePdf(invoiceData);
      setPdfData(pdfResult);
      setShowPdfModal(true);
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      toast.error('Failed to generate invoice PDF. Please try again later.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <Container fluid className="py-3">
      <Row>
        <Col>
          <Card className="shadow">
            <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">
                Invoices - Click on an Invoice to View PDF
              </h6>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0">From:</Form.Label>
                    <DatePicker
                      selected={dateRange.startDate}
                      onChange={date => setDateRange(prev => ({ ...prev, startDate: date }))}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                    />
                    <Form.Label className="mx-2 mb-0">To:</Form.Label>
                    <DatePicker
                      selected={dateRange.endDate}
                      onChange={date => setDateRange(prev => ({ ...prev, endDate: date }))}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Inv#</th>
                    <th>Date</th>
                    <th>PO#</th>
                    <th>Due Date</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr 
                      key={invoice.TxnID} 
                      onClick={() => !loadingPdf && handleInvoiceClick(invoice)} 
                      style={{ 
                        cursor: loadingPdf ? 'wait' : 'pointer',
                        opacity: loadingPdf ? 0.6 : 1
                      }}
                    >
                      <td>{invoice.RefNumber}</td>
                      <td>{moment(invoice.TimeCreated).format('YYYY-MM-DD')}</td>
                      <td>{invoice.PONumber}</td>
                      <td>
                        <span className={
                          invoice.BalanceRemaining !== 0 && moment(invoice.DueDate).isBefore(moment())
                            ? 'bg-danger text-white px-2 py-1 rounded-pill'
                            : ''
                        }>
                          {moment(invoice.DueDate).format('YYYY-MM-DD')}
                        </span>
                      </td>
                      <td className="text-end">{formatCurrency(invoice.Subtotal)}</td>
                      <td className="text-end">
                        {invoice.BalanceRemaining === 0 && !invoice.Memo?.startsWith('VOID:') ? (
                          <span className="text-success fw-bold">PAID</span>
                        ) : invoice.Memo?.startsWith('VOID:') ? (
                          <span className="text-danger fw-bold">VOID</span>
                        ) : (
                          formatCurrency(invoice.BalanceRemaining)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Loading indicator */}
      {loadingPdf && (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Generating PDF...</span>
          </div>
          <p className="mt-2">Generating invoice PDF...</p>
        </div>
      )}

      {/* Invoice PDF Modal */}
      <InvoicePdfModal
        show={showPdfModal}
        onHide={() => {
          setShowPdfModal(false);
          setPdfData(null);
        }}
        pdfData={pdfData}
        invoiceNumber={selectedInvoice?.RefNumber}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        document={selectedDocument}
      />
    </Container>
  );
}

export default Invoices; 